import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MailX, Send, CheckCircle2 } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t } from '../i18n';
import { Card, Spinner } from './ui';

/**
 * How far patients have got with their own accounts.
 *
 * The number that matters is the first one: a patient with no address on file
 * cannot be invited at all, and until now nobody found out unless they opened
 * that patient's record. Counting them is what turns "some patients probably
 * cannot log in" into a figure somebody can act on.
 *
 * The bar is drawn from the same three states as the badges in Pacientes, so
 * the dashboard and the list never disagree. patient_access_stats() scopes
 * itself: admin gets the practice, a professional gets their own roster.
 */

const BUCKETS = [
  { key: 'no_email', tone: 'bg-amber-400', text: 'text-amber-700', icon: MailX, label: 'patients.access.none' },
  { key: 'invited', tone: 'bg-sky-400', text: 'text-sky-700', icon: Send, label: 'patients.access.invited' },
  { key: 'active', tone: 'bg-emerald-500', text: 'text-emerald-700', icon: CheckCircle2, label: 'patients.access.active' },
];

const PatientAdoption = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc('patient_access_stats');
      if (!cancelled) { setStats(data ?? null); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <Card title={t('adoption.title')}><Spinner /></Card>;

  const total = stats?.total ?? 0;
  // Percentages are only meaningful once there is something to divide by.
  const share = (value) => (total === 0 ? 0 : Math.round((value / total) * 100));

  return (
    <Card title={t('adoption.title')} subtitle={t('adoption.subtitle')}>
      {total === 0 ? (
        <p className="text-sm text-slate-500">{t('adoption.none')}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100">
            {BUCKETS.map(({ key, tone }) => (
              stats[key] > 0 && (
                <div key={key} className={tone} style={{ width: `${share(stats[key])}%` }} />
              )
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {BUCKETS.map(({ key, text, icon: Icon, label }) => (
              <div key={key}>
                <p className={`flex items-center gap-1.5 text-xs font-medium ${text}`}>
                  <Icon size={13} />{t(label)}
                </p>
                <p className="text-2xl font-bold text-slate-900">{stats[key]}</p>
                <p className="text-xs text-slate-500">
                  {t('adoption.ofTotal', { percent: share(stats[key]), total })}
                </p>
              </div>
            ))}
          </div>

          {stats.no_email > 0 && (
            <p className="text-xs text-slate-500">
              {t('adoption.hint')}{' '}
              <Link to="/pacientes" className="font-medium text-indigo-600 hover:underline">
                {t('nav.patients')}
              </Link>
            </p>
          )}
        </div>
      )}
    </Card>
  );
};

export default PatientAdoption;
